// Product-page engraving panel (snippets/apts-engraving.liquid).
// Panel inputs are tied to the product form with the `form` attribute, so Horizon's own
// add-to-cart sends them as line-item properties (logo files upload with the same request).
// The engraving charge line is added/kept in sync by assets/apts-giftwrap.js.
// ?edit=<cart line key> (the cart's "Edit engraving" link) fills the panel from that line;
// once the edited item is added, the old line is removed and the shopper goes back to the cart.

import { StandardEvents } from '@shopify/events';

const root = document.querySelector('[data-apts-eng]');

if (root) {
  const productForm = () =>
    root.closest('.shopify-section')?.querySelector('product-form-component form') ||
    document.querySelector('product-form-component form');
  const card = root.querySelector('[data-eng-card]');
  const sides = [...root.querySelectorAll('[data-eng-side]')];
  const ptext = root.querySelector('[data-eng-ptext]');
  const pimg = root.querySelector('[data-eng-pimg]');
  const plogo = root.querySelector('[data-eng-plogo]');
  const plate = root.querySelector('.apts-eng__plate');
  const unit = Number(root.dataset.price) || 0;
  const base = Number(root.dataset.base) || 0;
  const field = (name) => root.querySelector(`[data-eng-field="${name}"]`);
  let previewSide = sides[0]?.dataset.engSide || 'front';
  const urls = {};
  const kept = {};
  const editKey = new URLSearchParams(window.location.search).get('edit');

  const money = (cents) => 'R' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 });

  const on = () => root.querySelector('[data-eng-choice]:checked')?.value === 'yes';
  const sideOn = (s) => s.querySelector('[data-eng-on]').checked;
  const sideType = (s) => s.querySelector('[data-eng-type]:checked')?.value || 'text';
  const qty = () => Math.max(1, Number(productForm()?.querySelector('input[name="quantity"]')?.value) || 1);

  function bindForm() {
    const form = productForm();
    if (!form) return null;
    // form.id is shadowed by the form's <input name="id">, so use the attribute.
    if (!form.getAttribute('id')) form.setAttribute('id', 'apts-product-form');
    const formId = form.getAttribute('id');
    root.querySelectorAll('[data-eng-field]').forEach((el) => el.setAttribute('form', formId));
    return form;
  }

  function fit() {
    if (ptext.hidden) return;
    const box = plate.getBoundingClientRect();
    const shape = plate.dataset.shape || 'round';
    // Engraving area as a share of the plate: [width, height]
    // Round plates use the square inside the circle, less a margin, so the gap to the rim is even all round.
    const area = { round: [0.62, 0.62], 'round-window': [0.62, 0.28], rectangle: [0.82, 0.74] }[shape] || [0.62, 0.62];
    const maxW = box.width * area[0];
    const maxH = box.height * area[1] * (plate.classList.contains('apts-eng__plate--both') ? 0.5 : 1);
    ptext.style.maxWidth = maxW + 'px';
    const fits = () => ptext.scrollHeight <= maxH + 1 && ptext.scrollWidth <= maxW + 1;
    let lo = 9;
    let hi = 44;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      ptext.style.fontSize = mid + 'px';
      if (fits()) lo = mid; else hi = mid - 1;
    }
    ptext.style.fontSize = lo + 'px';
  }

  function renderPreview() {
    const s = sides.find((x) => x.dataset.engSide === previewSide) || sides[0];
    if (!s) return;
    // A side's preview tab only works once that side is ticked and has text or a logo.
    const ready = (x) => {
      if (!sideOn(x)) return false;
      const t = sideType(x);
      const hasLogo = !!x.querySelector('[data-eng-field="logo"]').files?.length || !!kept[x.dataset.engSide];
      const hasText = !!x.querySelector('textarea').value.trim();
      return t === 'logo' ? hasLogo : t === 'text' ? hasText : hasLogo || hasText;
    };
    root.querySelectorAll('[data-eng-ptab]').forEach((b) => {
      const side = sides.find((x) => x.dataset.engSide === b.dataset.engPtab);
      b.disabled = !(side && ready(side)) && b.dataset.engPtab !== previewSide;
      b.classList.toggle('is-on', b.dataset.engPtab === previewSide);
    });
    const shape = s.dataset.shape || 'round';
    plate.dataset.shape = shape;
    plate.className = plate.className.replace(/apts-eng__plate--\S+/g, '').trim() + ' apts-eng__plate--' + shape;
    const type = sideType(s);
    const wantLogo = type !== 'text';
    const wantText = type !== 'logo';
    const url = urls[previewSide];
    plate.classList.toggle('apts-eng__plate--both', type === 'both');
    pimg.hidden = !(wantLogo && url);
    if (wantLogo && url) pimg.src = url;
    plogo.hidden = !(wantLogo && !url);
    ptext.hidden = !wantText;
    if (wantText) {
      const txt = s.querySelector('textarea').value.trim();
      ptext.textContent = txt || 'Your text here';
      const f = (s.querySelector('[data-eng-field="font"]:checked')?.dataset.font || '').split(';font-weight:');
      ptext.style.fontFamily = f[0];
      ptext.style.fontWeight = f[1] || '';
    }
    plate.classList.toggle('is-off', !sideOn(s));
    requestAnimationFrame(fit);
  }

  function update() {
    const active = on();
    card.hidden = !active;
    const chosen = [];
    sides.forEach((s) => {
      const enabled = active && sideOn(s);
      const type = sideType(s);
      s.querySelector('[data-eng-body]').hidden = !enabled;
      s.querySelector('[data-eng-textwrap]').hidden = type === 'logo';
      s.querySelector('[data-eng-logowrap]').hidden = type === 'text';
      s.querySelectorAll('[data-eng-field="text"],[data-eng-field="font"]').forEach((el) => {
        el.disabled = !(enabled && type !== 'logo');
      });
      const own = enabled && type !== 'logo' && !!s.querySelector('[data-eng-ownfont]')?.checked;
      const ownWrap = s.querySelector('[data-eng-ownfontwrap]');
      if (ownWrap) {
        ownWrap.hidden = !own;
        const fn = ownWrap.querySelector('input');
        fn.disabled = !own;
        fn.required = own;
      }
      const logo = s.querySelector('[data-eng-field="logo"]');
      logo.disabled = !(enabled && type !== 'text');
      const ta = s.querySelector('textarea');
      ta.required = enabled && type !== 'logo';
      const k = kept[s.dataset.engSide];
      logo.required = enabled && type !== 'text' && !k;
      if (k) {
        k.hidden.disabled = logo.disabled;
        logo.name = logo.files?.length ? k.name : '';
      }
      s.querySelector('[data-eng-count]').textContent = ta.value.length;
      if (enabled) chosen.push(`${s.dataset.label} (${type === 'both' ? 'text + logo' : type})`);
    });

    ['summary', 'detail', 'id', 'sides', 'variant', 'agree', 'notes', 'quote'].forEach((n) => {
      const el = field(n);
      if (el) el.disabled = !active;
    });
    const any = active && chosen.length > 0;
    root.querySelectorAll('[data-eng-more]').forEach((el) => { el.hidden = !any; });
    field('agree').required = any;
    field('summary').value = chosen.length ? `Added (${chosen.map((c) => c.replace(/ \(.*\)/, '')).join(' + ')})` : '';
    field('detail').value = chosen.join(' + ');
    field('sides').value = String(chosen.length);
    root.querySelector('[data-eng-notescount]').textContent = field('notes').value.length;

    const firstToggle = sides[0]?.querySelector('[data-eng-on]');
    firstToggle?.setCustomValidity(active && chosen.length === 0 ? 'Choose at least one side to engrave.' : '');

    const n = chosen.length;
    const q = qty();
    const per = unit * n;
    root.querySelector('[data-eng-sumtext]').textContent = n
      ? `Engraving: ${chosen.map((c) => c.replace(/ \(.*\)/, '')).join(' + ')}${q > 1 ? ` × ${q} items` : ''}`
      : 'Choose at least one side to engrave';
    root.querySelector('[data-eng-sumprice]').textContent = !n ? '' : unit
      ? `+${money(per * q)}${q > 1 ? ` (${money(base + per)} each)` : ''}`
      : 'Quote to follow';
    const cur = sides.find((x) => x.dataset.engSide === previewSide);
    if (cur && !sideOn(cur)) previewSide = (sides.find(sideOn) || sides[0]).dataset.engSide;
    renderPreview();
  }

  root.addEventListener('input', (e) => {
    const s = e.target.closest('[data-eng-side]');
    if (s) previewSide = s.dataset.engSide;
    update();
  });

  root.addEventListener('change', (e) => {
    const t = e.target;
    const s = t.closest('[data-eng-side]');
    if (s) previewSide = s.dataset.engSide;
    if (t.matches('[data-eng-field="logo"]')) setFile(s, t);
    if (t.matches('[data-eng-choice]') && on()) bindForm();
    update();
  });

  function setFile(s, input) {
    const f = input.files?.[0];
    const key = s.dataset.engSide;
    const title = s.querySelector('[data-eng-fname]');
    const hint = s.querySelector('[data-eng-fhint]');
    const box = s.querySelector('[data-eng-filebox]');
    const note = s.querySelector('[data-eng-filenote]');
    if (urls[key]) URL.revokeObjectURL(urls[key]);
    delete urls[key];
    let error = '';
    if (f && !/\.(svg|ai|eps|pdf)$/i.test(f.name)) error = 'Please upload a vector file (SVG, AI, EPS or PDF)';
    else if (f && f.size > 2 * 1024 * 1024) error = 'That file is over 2MB. Please upload a smaller one';
    if (error) input.value = '';
    const ok = !!input.files?.length;
    if (ok && kept[key]) dropKept(key, input);
    if (ok && /\.svg$/i.test(f.name)) urls[key] = URL.createObjectURL(f);
    title.textContent = error || (ok ? 'Change file' : 'Upload your logo');
    title.classList.toggle('is-error', !!error);
    hint.hidden = ok;
    box.hidden = !ok;
    note.hidden = !ok;
    s.querySelector('[data-eng-filename]').textContent = ok ? f.name : '';
    if (!ok && kept[key]) showKept(s);
  }

  function showKept(s) {
    const k = kept[s.dataset.engSide];
    s.querySelector('[data-eng-fname]').textContent = 'Change file';
    s.querySelector('[data-eng-fhint]').hidden = true;
    s.querySelector('[data-eng-filebox]').hidden = false;
    s.querySelector('[data-eng-filenote]').hidden = true;
    s.querySelector('[data-eng-filename]').textContent = decodeURIComponent(k.url.split('?')[0].split('/').pop());
  }

  function dropKept(key, input) {
    input.name = kept[key].name;
    kept[key].hidden.remove();
    if (urls[key] === kept[key].url) delete urls[key];
    delete kept[key];
  }

  async function startEdit() {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const line = cart.items.find((i) => i.key === editKey);
    if (!line || !Number(line.properties?._engrave_sides)) return;
    const pp = line.properties;
    const form = bindForm();
    root.querySelector('[data-eng-choice][value="yes"]').checked = true;
    sides.forEach((s) => {
      const label = s.dataset.label;
      const text = pp[`_${label} text`] || '';
      const logo = pp[`_${label} logo`] || '';
      s.querySelector('[data-eng-on]').checked = !!(text || logo);
      const type = text && logo ? 'both' : logo ? 'logo' : 'text';
      s.querySelector(`[data-eng-type][value="${type}"]`).checked = true;
      s.querySelector('textarea').value = text;
      const font = [...s.querySelectorAll('[data-eng-field="font"]')].find((r) => r.value === pp[`_${label} font`]);
      if (font) font.checked = true;
      const fontName = s.querySelector('[data-eng-field="fontname"]');
      if (fontName) fontName.value = pp[`_${label} font name`] || '';
      if (logo) {
        const input = s.querySelector('[data-eng-field="logo"]');
        const hidden = Object.assign(document.createElement('input'), { type: 'hidden', name: input.name, value: logo });
        if (form) hidden.setAttribute('form', form.getAttribute('id'));
        s.appendChild(hidden);
        kept[s.dataset.engSide] = { hidden, name: input.name, url: logo };
        if (/\.svg(\?|$)/i.test(logo)) urls[s.dataset.engSide] = logo;
        showKept(s);
      }
    });
    field('notes').value = pp['_Engraving notes'] || '';
    const q = form?.querySelector('input[name="quantity"]');
    if (q) q.value = line.quantity;
    root.querySelector('[data-eng-editing]').hidden = false;
    update();
    root.scrollIntoView({ block: 'start', behavior: 'smooth' });

    let done = false;
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(async ({ detail } = {}) => {
        if (done || detail?.didError || String(detail?.source || '').startsWith('apts')) return;
        done = true;
        const now = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const old = now.items.find((i) => i.key === editKey);
        if (old) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: editKey, quantity: old.quantity > line.quantity ? old.quantity - line.quantity : 0 })
          });
        }
        window.location.href = '/cart';
      }).catch(() => {});
    });
  }

  root.addEventListener('focusin', (e) => {
    const s = e.target.closest('[data-eng-side]');
    if (s && s.dataset.engSide !== previewSide) {
      previewSide = s.dataset.engSide;
      renderPreview();
    }
  });

  root.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-eng-fileremove]');
    if (rm) {
      const s = rm.closest('[data-eng-side]');
      const input = s.querySelector('[data-eng-field="logo"]');
      if (kept[s.dataset.engSide]) dropKept(s.dataset.engSide, input);
      input.value = '';
      setFile(s, input);
      update();
      return;
    }
    const tab = e.target.closest('[data-eng-ptab]');
    if (!tab || tab.disabled) return;
    previewSide = tab.dataset.engPtab;
    renderPreview();
  });

  // Quantity changes (corporate orders) update the running total.
  document.addEventListener('click', (e) => { if (e.target.closest('quantity-selector-component')) setTimeout(update, 0); });
  document.addEventListener('change', (e) => { if (e.target.matches?.('input[name="quantity"]')) update(); });
  document.addEventListener('input', (e) => { if (e.target.matches?.('input[name="quantity"]')) update(); });

  // A fresh id per add, so each engraved line gets its own engraving charge line.
  document.addEventListener('submit', (e) => {
    if (e.target === productForm() && on()) field('id').value = 'e' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }, true);

  window.addEventListener('resize', () => requestAnimationFrame(fit));
  document.fonts?.ready.then(fit);
  bindForm();
  update();
  if (editKey) startEdit();
}
